import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Modal from "react-modal";
import { validateToken } from "../tokenValidation";
import { format } from 'date-fns';

const RecordDetailsPage = (prop) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ base64Data: null, url: null });
  const record = prop.record;

  useEffect(() => {
    if (record) {
      validateToken(navigate);
    }
  }, [navigate, record]);

  if (!record) {
    return <p>No record data available.</p>;
  }

  const {
    facilityName,
    wellnessRecordDetails,
    prescriptionDetails,
    immunizationDetails,
    recordArtifact,
    clinicalConsultationReport,
    dischargeSummeryDetails,
    invoiceDetails,
    diagnosticReportDetails,
    mobileNumber
  } = record;

  const handleDocumentClick = (artifact) => {
    let base64Data = null;
    let fileName = artifact?.title || "attachment";

    if (artifact.base64Data) {
      base64Data = artifact.base64Data;
    } else if (artifact.databytes) {
      base64Data = artifact.databytes;
    } else if (artifact.data) {
      base64Data = artifact.data;
    }

    if (base64Data) {
      const link = document.createElement("a");
      link.href = `data:${artifact.contentType};base64,${base64Data}`;
      link.download = `${fileName}.${getExtensionFromContentType(artifact.contentType)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (artifact.url) {
      window.open(artifact.url, "_blank");
    } else {
      toast.error("No document data available to download.");
    }
  };

  const getExtensionFromContentType = (type) => {
    if (!type) return "bin";
    const map = {
      "application/pdf": "pdf",
      "image/jpeg": "jpg",
      "image/png": "png",
    };
    return map[type] || "bin";
  };


  const renderDetails = (details, mobileNumber) => {
    if (!details) return null;

    const { clinicName, practitionerName, practitionerDesignation, patientName, gender, dob, visitDate, abhaAddress, abhaNumber } = details;

    return (
      <div className="grid grid-cols-2 gap-4 mb-6 border bg-gray-100 p-4 rounded-lg shadow-lg">
        <div>
          <p><strong className="text-teal-700">Patient Name:</strong> {patientName}</p>
          <p><strong className="text-teal-700">Date of Birth:</strong> {new Date(dob).toLocaleDateString()}</p>
          <p><strong className="text-teal-700">Gender:</strong> {gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : 'N/A'}</p>
          <p><strong className="text-teal-700">ABHA Address:</strong> {abhaAddress || 'N/A'}</p>
          <p><strong className="text-teal-700">ABHA Number:</strong> {abhaNumber || 'N/A'}</p>
          <p><strong className="text-teal-700">Mobile Number:</strong> {mobileNumber || 'N/A'}</p>
        </div>

        <div>
          <p><strong className="text-teal-700">Clinic Name:</strong> {clinicName || 'N/A'}</p>
          <p><strong className="text-teal-700">Practitioner Name:</strong> {practitionerDesignation ? `${practitionerDesignation} ` : ''} {practitionerName}</p>
          <p>
            <strong className="text-teal-700">Visit Date:</strong>{" "}
            {visitDate
              ? format(
                new Date(visitDate.replace("DateTimeType[", "").replace("]", "")),
                "dd-MMMM-yyyy hh:mma"
              )
              : "N/A"}
          </p>
        </div>
      </div>
    );
  };


  const renderObservations = (observations) => {
    if (!observations?.length) return null;
    return (
      <div className=" border bg-gray-100 p-4 rounded-lg shadow-lg">
        <h3 className="font-bold text-teal-700">Observations:</h3>
        <ul className="list-disc ml-5">
          {observations.map((obs, index) => (
            <li key={index}>
              <span className="font-bold">{obs.observationName}</span> : {obs.value ? obs.value : ''} {obs.unit ? `(${obs.unit})` : ""}  <span>(Status - {obs.status})</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderAttachments = (attachments) => {
    if (!attachments?.length) return null;

    return (
      <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-4">
        <h3 className="text-lg font-semibold mb-4 text-teal-700">Attachments</h3>
        {attachments.map((attachment, index) => (
          <div
            key={index}
            className="cursor-pointer bg-gray-100 p-4 rounded-md shadow-md my-4 hover:bg-gray-200"
            onClick={() => handleDocumentClick(attachment)}
          >
            <p className="font-medium flex justify-between items-center">
              <span>Document: {attachment.title || "Attachment"}</span>
              <span className="ml-2">&#8594;</span>
            </p>
          </div>
        ))}
      </div>
    );
  };



  return (
    <div className="bg-[#F6F6F6] min-h-screen p-6">
      <h1 className="text-2xl font-bold mb-4 text-gray-800">Record Details</h1>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div>
          <p><strong className="text-teal-700">Facility / Clinic Name :</strong> {facilityName}</p>
        </div>

        {recordArtifact && (
          <>
            <h2 className="text-xl font-semibold mt-6  mb-3 text-gray-600">Record Artifact</h2>
            {renderDetails(recordArtifact, mobileNumber)}
            {renderAttachments([recordArtifact])}
          </>
        )}

        {wellnessRecordDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-600">Wellness Record Details</h2>
            {renderDetails(wellnessRecordDetails, mobileNumber)}
            {renderObservations(wellnessRecordDetails.observations)}
            {renderAttachments(wellnessRecordDetails.attachments)}
          </>
        )}

        {prescriptionDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6  mb-3 text-gray-600">Prescription Details</h2>
            {renderDetails(prescriptionDetails, mobileNumber)}
            <div className=" border bg-gray-100 p-4 rounded-lg shadow-lg">
              <h3 className="font-bold mt-4 text-teal-700 mb-2">Medications:</h3>
              <table className="table-auto border-collapse border border-gray-200">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">Medication Name</th>
                    <th className="border border-gray-300 px-4 py-2">Dosage Instruction</th>
                    <th className="border border-gray-300 px-4 py-2">Reason</th>
                    <th className="border border-gray-300 px-4 py-2">Route</th>
                    <th className="border border-gray-300 px-4 py-2">Timing</th>
                    <th className="border border-gray-300 px-4 py-2">How to Take</th>
                  </tr>
                </thead>
                <tbody>
                  {prescriptionDetails.medicationRequests?.map((med, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-4 py-2">{med.medicationName || "N/A"}</td>
                      <td className="border border-gray-300 px-4 py-2">{med.dosageInstruction || "N/A"}</td>
                      <td className="border border-gray-300 px-4 py-2">{med.reason || "N/A"}</td>
                      <td className="border border-gray-300 px-4 py-2">{med.route || "N/A"}</td>
                      <td className="border border-gray-300 px-4 py-2">{med.timing || "N/A"}</td>
                      <td className="border border-gray-300 px-4 py-2">{med.howToTake || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {renderAttachments(prescriptionDetails.attachments)}
          </>
        )}

        {immunizationDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6  mb-3 text-gray-600">Immunization Details</h2>
            {renderDetails(immunizationDetails, mobileNumber)}
            <div className="space-y-4">
              {immunizationDetails.immunizationDetails?.map((immunization, index) => (
                <div key={index} className="border bg-gray-100 p-4 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold mb-2"> <span className="text-teal-700">Vaccine Name: </span>{immunization.vaccineName || "N/A"}</h3>
                  <p><strong>Lot Number:</strong> {immunization.lotNumber || "N/A"}</p>
                  <p><strong>Occurrence Date:</strong> {immunization.occurrenceDate || "N/A"}</p>
                  <p><strong>Vaccine Manufacturer:</strong> {immunization.vaccineManufacturer || "N/A"}</p>
                  <p><strong>Vaccine Dose Number:</strong> {immunization.vaccineDoseNumber || "N/A"}</p>
                </div>
              ))}
            </div>
            {renderAttachments(immunizationDetails.attachments)}
          </>
        )}

        {clinicalConsultationReport && (
          <>
            <h2 className="text-xl font-semibold mt-6  mb-3 text-gray-600">OP Consultation Details</h2>
            {renderDetails(clinicalConsultationReport, mobileNumber)}
            {/* Rendering Medical Conditions dynamically */}
            {clinicalConsultationReport.medicalCondition && clinicalConsultationReport.medicalCondition.length > 0 && (
              <div className=" border bg-gray-100 p-4 rounded-lg shadow-lg">
                <h3 className="text-lg font-semibold mt- mb-3 text-teal-700">Medical Conditions and History</h3>
                <ul>
                  {clinicalConsultationReport.medicalCondition.map((condition, index) => (
                    <li key={index} className="mb-2">
                      <div>
                        <strong>{condition.medicalConditionName}</strong>, Test Date :{" "}
                        {new Date(condition.reportedDate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}


            {/* Rendering other observations dynamically */}
            {clinicalConsultationReport.otherObservations &&
              clinicalConsultationReport.otherObservations.length > 0 && (
                <div className="mt-6 border bg-gray-100 p-4 rounded-lg shadow-lg">
                  <h3 className="text-lg font-semibold mb-3 text-teal-700">Observations</h3>
                  {clinicalConsultationReport.otherObservations.map((observation, index) => (
                    <div key={index} className="mb-1">
                      <strong>{observation.key} : </strong>
                      {observation.value}
                    </div>
                  ))}
                </div>
              )}

            {clinicalConsultationReport.medicationRequests &&
              clinicalConsultationReport.medicationRequests.length > 0 && (
                <div className="mt-6 border bg-gray-100 p-4 rounded-lg shadow-lg">
                  <h3 className="font-bold mt-4 text-teal-700 mb-2">Medications:</h3>
                  <table className="table-auto border-collapse border border-gray-200">
                    <thead>
                      <tr>
                        <th className="border border-gray-300 px-4 py-2">Medication Name</th>
                        <th className="border border-gray-300 px-4 py-2">Dosage Instruction</th>
                        <th className="border border-gray-300 px-4 py-2">Reason</th>
                        <th className="border border-gray-300 px-4 py-2">Route</th>
                        <th className="border border-gray-300 px-4 py-2">Timing</th>
                        <th className="border border-gray-300 px-4 py-2">How to Take</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clinicalConsultationReport.medicationRequests?.map((med, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2">{med.medicationName || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.dosageInstruction || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.reason || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.route || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.timing || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.howToTake || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}


            {clinicalConsultationReport.allergyDetails && clinicalConsultationReport.allergyDetails.length > 0 && (
              <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Allergy Details</h3>
                <ul>
                  {clinicalConsultationReport.allergyDetails.map((allergy, index) => (
                    <li key={index} className="mb-2">
                      <div>
                        <strong>{allergy.details}</strong>
                      </div>
                      <div>
                        Status: {allergy.status}
                      </div>
                      <div>
                        Verified: {allergy.verificationStatus}
                      </div>
                      <div>
                        Reported on:{" "}
                        {allergy.reportedDate
                          ? format(new Date(allergy.reportedDate), "dd-MMM-yyyy hh:mma")
                          : "N/A"}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {clinicalConsultationReport.procedureDetails && clinicalConsultationReport.procedureDetails.length > 0 && (
              <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Procedure Details</h3>
                <ul>
                  {clinicalConsultationReport.procedureDetails.map((procedure, index) => (
                    <li key={index} className="mb-2">
                      <div>
                        <strong>{procedure.name}</strong>
                      </div>
                      <div>
                        Status : <i>{procedure.outcome ? procedure.outcome : 'N/A'}</i>
                      </div>
                      <div>
                        Reported Date :<i>{" "}
                          {procedure.reportedDate
                            ? format(new Date(procedure.reportedDate), "dd-MMM-yyyy hh:mma")
                            : "N/A"}</i>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {renderAttachments(clinicalConsultationReport.attachments)}
          </>
        )}

        {dischargeSummeryDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-600">Discharge Summery Details</h2>
            {renderDetails(dischargeSummeryDetails, mobileNumber)}
            <div>
              {dischargeSummeryDetails.allergyDetails && dischargeSummeryDetails.allergyDetails.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-3">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Allergy Details</h3>
                  <ul>
                    {dischargeSummeryDetails.allergyDetails.map((allergy, index) => (
                      <li key={index} className="mb-2">
                        <div>
                          <strong>{allergy.details}</strong>
                        </div>
                        <div>
                          Status: {allergy.status}
                        </div>
                        <div>
                          Verified: {allergy.verificationStatus}
                        </div>
                        <div>
                          Reported on:{" "}
                          {allergy.reportedDate
                            ? format(new Date(allergy.reportedDate), "dd-MMM-yyyy hh:mma")
                            : "N/A"}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {dischargeSummeryDetails.medicalCondition && dischargeSummeryDetails.medicalCondition.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Medical Conditions and History</h3>
                  <ul>
                    {dischargeSummeryDetails.medicalCondition.map((condition, index) => (
                      <li key={index} className="mb-2">
                        <div>
                          <strong>{condition.medicalConditionName}</strong>, Test Date :{" "}
                          {condition.reportedDate
                            ? format(new Date(condition.reportedDate), "dd-MMM-yyyy hh:mma")
                            : "N/A"}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {dischargeSummeryDetails.medicationRequest && dischargeSummeryDetails.medicationRequest.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Medication Request</h3>

                  <table className="table-auto border-collapse border border-gray-200">
                    <thead>
                      <tr>
                        <th className="border border-gray-300 px-4 py-2">Medication Name</th>
                        <th className="border border-gray-300 px-4 py-2">Dosage Instruction</th>
                        <th className="border border-gray-300 px-4 py-2">Reason</th>
                        <th className="border border-gray-300 px-4 py-2">Route</th>
                        <th className="border border-gray-300 px-4 py-2">Timing</th>
                        <th className="border border-gray-300 px-4 py-2">How to Take</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dischargeSummeryDetails.medicationRequest?.map((med, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2">{med.medicationName || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.dosageInstruction || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.reason || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.route || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.timing || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{med.howToTake || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {dischargeSummeryDetails.observationDetails && dischargeSummeryDetails.observationDetails.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Observation Details</h3>
                  <ul>
                    {dischargeSummeryDetails.observationDetails.map((observation, index) => (
                      <li key={index} className="mb-2">
                        <div>
                          <strong>{observation.observationName}</strong>:{" "}
                          <i>
                            {observation.value}{" "}
                            {observation.unit ? `(${observation.unit})` : ""}
                          </i>
                          {observation.measureDate && (
                            <>
                              , Date:{" "}
                              <i>{format(new Date(observation.measureDate), "dd-MMM-yyyy hh:mma")}</i>
                            </>
                          )}
                          {observation.status && (
                            <>
                              , Status: <i>{observation.status}</i>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {dischargeSummeryDetails.procedureDetails && dischargeSummeryDetails.procedureDetails.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Procedure Details</h3>
                  <ul>
                    {dischargeSummeryDetails.procedureDetails.map((procedure, index) => (
                      <li key={index} className="mb-2">
                        <div>
                          <strong>{procedure.details || procedure.name}</strong>
                        </div>
                        <div>
                          Status : <i>{procedure.outcome ? procedure.outcome : 'N/A'}</i>
                        </div>
                        <div>
                          Reported Date :<i>{" "}
                            {procedure.reportedDate
                              ? format(new Date(procedure.reportedDate), "dd-MMM-yyyy hh:mma")
                              : "N/A"}</i>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {renderAttachments(dischargeSummeryDetails.attachments)}
          </>
        )}


        {invoiceDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-600">Invoice Details</h2>
            {renderDetails(invoiceDetails, mobileNumber)}
            <div>
              {invoiceDetails.invoiceDetails && invoiceDetails.invoiceDetails.length > 0 && (
                <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-6">
                  <h3 className="text-lg font-semibold mt-3 mb-3 text-teal-700">Invoice Items</h3>
                  <table className="table-auto border-collapse border border-gray-200">
                    <thead>
                      <tr>
                        <th className="border border-gray-300 px-4 py-2">Item Name</th>
                        <th className="border border-gray-300 px-4 py-2">Quantity</th>
                        <th className="border border-gray-300 px-4 py-2">Rate</th>
                        <th className="border border-gray-300 px-4 py-2">Discount</th>
                        <th className="border border-gray-300 px-4 py-2">CGST</th>
                        <th className="border border-gray-300 px-4 py-2">SGST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetails.invoiceDetails.map((item, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2">{item.itemName || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{item.quantity || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{item.rate || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{item.discount || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{item.cgst || "N/A"}</td>
                          <td className="border border-gray-300 px-4 py-2">{item.sgst || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mb-2 mt-2">
                    <strong className="text-teal-700">Total Net:</strong> {invoiceDetails.totalNet || 'N/A'}
                  </div>
                  <div className="mb-2">
                    <strong className="text-teal-700">Total Gross:</strong> {invoiceDetails.totalGross || 'N/A'}
                  </div>

                </div>
              )}
            </div>

            {renderAttachments(invoiceDetails.attachments)}
          </>
        )}


        {diagnosticReportDetails && (
          <>
            <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-600">Diagnostic Report Details</h2>
            {renderDetails(diagnosticReportDetails, mobileNumber)}

            {diagnosticReportDetails.observations && diagnosticReportDetails.observations.length > 0 && (
              <div className="border bg-gray-100 p-4 rounded-lg shadow-lg mt-4">
                <h3 className="font-bold text-teal-700">Test Observations:</h3>
                <ul className="list-disc ml-5">
                  {diagnosticReportDetails.observations.map((obs, index) => (
                    <li key={index}>
                      <span className="font-bold text-teal-700">{obs.testName}</span> : <span className="font-bold">{obs.value ? obs.value : 'N/A'} {obs.unit ? `${obs.unit}` : ""}</span>
                      {obs.referenceRange && (
                        <i>{` (Reference Range: ${obs.referenceRange})`}</i>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {renderAttachments(diagnosticReportDetails.attachments)}
          </>
        )}



      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        ariaHideApp={false}
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
      >
        <div className="bg-white rounded-lg p-6 shadow-lg relative max-w-4xl w-full">
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
          >
            ✖
          </button>
          <h2 className="text-xl font-bold mb-4">Document Viewer</h2>
          {modalContent.contentType === "application/pdf" && modalContent.base64Data && (
            <iframe
              src={modalContent.base64Data}
              title="PDF Viewer"
              className="w-full h-96 border rounded-md"
            />
          )}
          {(modalContent.contentType === "image/jpeg" || modalContent.contentType === "image/png") &&
            modalContent.base64Data && (
              <img src={modalContent.base64Data} alt="Document" className="w-full h-auto rounded-md" />
            )}
          {modalContent.url && !modalContent.base64Data && (
            <iframe src={modalContent.url} title="Document" className="w-full h-96 border rounded-md" />
          )}
          {!modalContent.base64Data && !modalContent.url && <p>No document to display.</p>}
        </div>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default RecordDetailsPage;
